/* ============================================================
   ZNAKI · audio/accents.js
   Dźwięki akcydentalne ("ziarna") — warstwa grana na tle.

   Zdarzenia przychodzą z pętli sceny razem ze specyfikacjami
   rysowania (figures.js ustawia na nich znaczniki):
     spec.tik   — figura gwałtowna właśnie drgnęła
     spec.exit  — figura weszła w fazę znikania z planszy
     spec.glitch — duża pomarańczowa tarcza rozdziera się na pasy
                  (`fire` w klatce startu, `dur` = czas rozdarcia)
     spec.wave  — figura powolna faluje; znacznik trwa przez cały jej
                  pobyt na scenie, `fire` tylko w klatce startu
     fig.spinV  — figura obraca się równomiernie (wskazówka zegara)

   Każda brzmiąca figura dostaje WŁASNY łańcuch (AudioChain).
   Wszystkie jej dźwięki idą przez niego, więc głośność, panorama
   i filtr dolnoprzepustowy działają na całą figurę naraz:

     głos (tik / exit / zegar) → łańcuch figury → magistrala "accent" → master

   Panorama i głośność biorą się z tego, co widać: pozycja w poziomie
   → panorama, wielkość i krycie → głośność, głębia → barwa.

   Fale są jedynym dźwiękiem PRZECIĄGŁYM poza zegarami: pod falującą
   figurą gra jeden długi głos, od jej wejścia aż do chwili, gdy rusza
   do wyjścia — wtedy gaśnie. Próbka nawija się przy tym w kółko
   (zakładką na siebie, jak akordy tła), więc fala trwa dokładnie tyle,
   ile figura faluje, także gdy ta zostaje na scenie dłużej niż plik.
   Kolejne figury dostają kolejne pliki (banks.js),
   co splata z ich pojawiania się sugestię melodii; dlatego limity
   (flMaxVoices, flMinGap) pytają o plik dopiero po przejściu — figura
   odrzucona nie zabiera kolejnego dźwięku z linii.

   Glitch jest tu wyjątkiem podwójnym: próbka nie jest przypisana
   do figury (bierze się z wspólnej kolejki, patrz banks.js) i gra
   nawet na tarczy, której acDensity nie dopuściło do głosu — jest
   zbyt rzadki, żeby wypadało go zgubić. Za to nadal idzie łańcuchem
   figury, więc słychać go tam, gdzie tarcza jest na ekranie.

   Tożsamość dźwiękowa figury (bank, grupa wysokości, kolejka
   wariantów) mieszka na samej figurze — w fig._snd, obok _inT/_outT
   z figures.js. Dzięki temu figura, która wyszła z kadru i wróciła,
   gra dalej swoim głosem i swoją kolejką, a nie losuje ich od nowa.
   Łańcuch i głosy to zasoby chwilowe — powstają, gdy figura brzmi,
   i znikają, gdy wychodzi z kadru.
   ============================================================ */

import { PARAMS } from '../config';
import { clamp, clamp01, lerp, hash01, mulberry32 } from '../utils';
import AudioChain, { FILTER_OPEN } from './chain';

/* po tylu klatkach bez figury w kadrze zwijamy jej łańcuch */
const RELEASE_FRAMES = 3;
/* najdłuższe ziarno ma ~1.5 s — tyle łańcuch czeka, nim się odepnie */
const CHAIN_LINGER_MS = 2500;
/* widełki amplitudy ruchu własnego figur falujących: dolny koniec to
   najspokojniejszy `calm` (0.5 × 0.7), górny — najszerszy `drift`
   (2.1 × 1.3); patrz PERSONALITIES w figures.js */
const WAVE_AMP_MIN = 0.35;
const WAVE_AMP_MAX = 2.50;

class AccentLayer {

  /**
   * @param {AudioEngine} engine
   * @param {Banks} banks
   */
  constructor(engine, banks) {
    this.engine = engine;
    this.banks = banks;

    this.bus = engine.bus('accent', { volume: 0 });
    if (this.bus) this.bus.cancelRamps().setVolume(0, 0);

    this._figs = new Map();     /* fig → zasoby chwilowe (łańcuch, zegar, fala) */
    this._grains = [];          /* grające ziarna jednorazowe */
    this._clocks = 0;           /* ile pętli zegarowych gra teraz */
    this._waves = 0;            /* ile fal brzmi teraz */
    this._frame = 0;
    this._lastGrainAt = -1e9;
    this._lastWaveAt = -1e9;
    this._paused = false;
    this._destroyed = false;
  }

  /** wczytaj próbki w tle — po tle, żeby nie odbierać mu pasma */
  preload() {
    const urls = this.banks.allUrls();
    if (!urls.length) return Promise.resolve();
    return this.engine.loadSequential(urls);
  }

  /* --- klatka -------------------------------------------------- */

  /**
   * @param {Array} specs — specyfikacje rysowania z tej klatki
   * @param {object} st — stan chwili
   */
  update(specs, st) {
    if (this._destroyed || !this.bus || this._paused || !specs) return;
    const A = PARAMS.audio;
    this._frame++;

    /* poziom całej warstwy — ustawiany tutaj, a nie w pętli sceny,
       żeby nie deptać wyciszenia zrobionego przez pause() */
    this.bus.setVolume(A.acVolume, 0.05);

    for (const spec of specs) {
      const fig = spec.fig;
      const isClock = !!fig.spinV;
      /* glitch i fala trzymają łańcuch przez cały czas trwania, nie
         tylko w klatce startu — dzięki temu panorama nadąża za figurą */
      const glitching = !!spec.glitch;
      /* figures.js stawia znacznik na każdej powolnej figurze; o tym,
         czy ta akurat ma dla siebie próbkę, decyduje bank — pytamy
         zawczasu, żeby nie budować łańcucha figurze, która i tak
         nie zabrzmi */
      const waving = !!spec.wave && this.banks.canWave(fig);
      /* fala mogła jeszcze nie wybrzmieć, choć figura przestała falować —
         wtedy też wchodzimy, żeby ją zgasić */
      const held = this._figs.get(fig);
      if (!spec.tik && !spec.exit && !isClock && !glitching && !waving
          && !(held && held.wave)) continue;

      const snd = voiceOf(fig, this.banks, A);
      if (!snd.voiced && !glitching) continue;

      const rec = held || this._resources(fig);
      rec.seen = this._frame;

      /* łańcuch figury śledzi jej wygląd na ekranie */
      this._shape(rec, snd, spec, st, A);

      if (snd.voiced) {
        if (spec.tik)  this._tik(rec, snd, A);
        if (spec.exit) this._exit(rec, snd, A);
        if (isClock)   this._clock(rec, snd, A);
        if (waving && spec.wave.fire) this._waveStart(rec, snd, spec, st, A);
        else if (!waving && rec.wave) this._waveStop(rec, A);
      }
      if (glitching && spec.glitch.fire) this._glitch(rec, snd, spec.glitch, A);
    }

    this._sweep();
  }

  /* --- sterowanie ---------------------------------------------- */

  pause() {
    if (this._paused) return;
    this._paused = true;
    if (this.bus) this.bus.fade(0, 0.3);
  }

  resume() {
    if (!this._paused) return;
    this._paused = false;
    if (this.bus) this.bus.cancelRamps();
  }

  destroy() {
    this._destroyed = true;
    if (this.bus) this.bus.cancelRamps().setVolume(0, 0);
    for (const rec of this._figs.values()) this._dispose(rec, true);
    this._figs.clear();
    for (const v of this._grains) v.destroy();
    this._grains.length = 0;
    this._clocks = 0;
    this._waves = 0;
  }

  get state() {
    return {
      figs: this._figs.size, grains: this._grains.length,
      clocks: this._clocks, waves: this._waves,
    };
  }

  /* --- zasoby chwilowe figury ----------------------------------- */

  _resources(fig) {
    let rec = this._figs.get(fig);
    if (rec) return rec;

    const chain = new AudioChain(this.engine.ctx, { volume: 0 });
    chain.connect(this.bus);
    rec = {
      fig, chain, clock: null, wave: null,
      fresh: true, lastTikAt: -1e9, seen: this._frame,
    };
    this._figs.set(fig, rec);
    return rec;
  }

  /* głośność, panorama i barwa z obrazu figury */
  _shape(rec, snd, spec, st, A) {
    const fig = rec.fig;

    /* wielkość na ekranie [px] → udział w głośności */
    const dim = (fig.size.r ? fig.size.r * 2 : Math.max(fig.size.w, fig.size.h))
      * st.vminPx * spec.s;
    const sizeN = clamp01(dim / (st.vminPx * 1.2));

    const vol = lerp(A.acGainMin, 1, sizeN)      /* mała figura = cichy dźwięk */
      * lerp(0.4, 1, clamp01(spec.alpha))        /* figura widmowa = cichszy dźwięk */
      * lerp(0.65, 1, clamp01(spec.z))           /* figura w głębi = dalej */
      * snd.gainJit;                             /* stała wariacja tej figury */

    /* pozycja w poziomie → panorama */
    const pan = clamp((spec.x - st.cx) / (st.vwPx * 0.5), -1, 1) * A.acPanWidth;

    /* barwa: figury w głębi przymknięte; acCutoff to wspólny sufit */
    let hz = A.acCutoff > 0 ? A.acCutoff : FILTER_OPEN;
    if (A.acDepthTone > 0) {
      const dull = lerp(1200, FILTER_OPEN, clamp01(spec.z));
      hz = Math.min(hz, lerp(FILTER_OPEN, dull, clamp01(A.acDepthTone)));
    }

    /* pierwsza klatka figury ustawia się natychmiast — inaczej jej
       pierwszy tik wszedłby w trakcie narastania łańcucha */
    const tc = rec.fresh ? 0 : 0.08;
    rec.chain.setVolume(vol, tc).setPan(pan, tc).setCutoff(hz, rec.fresh ? 0 : 0.12);
    rec.fresh = false;
  }

  /* --- zdarzenia ------------------------------------------------ */

  /* tiknięcie: kolejny wariant z potasowanej kolejki grupy figury */
  _tik(rec, snd, A) {
    if (!snd.tiki) return;                       /* figura bez własnego banku */
    const now = this.engine.ctx.currentTime;
    if (now - rec.lastTikAt < A.acFigGap) return;
    if (!this._budget(now, A)) return;

    const order = snd.tiki.order;
    const url = order[snd.tikiIdx];
    snd.tikiIdx++;
    /* kolejka wyczerpana — tasujemy od nowa, bez powtórki na styku */
    if (snd.tikiIdx >= order.length) {
      snd.tiki.order = reshuffle(order, snd.rng, url);
      snd.tikiIdx = 0;
    }

    if (this._grain(rec, snd, url, A.acTikiVol, A)) rec.lastTikAt = now;
  }

  /* "zasysanie" tuż przed zniknięciem figury */
  _exit(rec, snd, A) {
    if (!snd.exitOrder) return;
    if (!this._budget(this.engine.ctx.currentTime, A)) return;

    const url = snd.exitOrder[snd.exitIdx];
    snd.exitIdx = (snd.exitIdx + 1) % snd.exitOrder.length;
    this._grain(rec, snd, url, A.acExitVol, A);
  }

  /* rozdarcie sygnału: próbka przycięta do czasu animacji.
     Pliki są kilkukrotnie dłuższe niż glitch, więc startujemy je
     z losowego miejsca — ta sama próbka za każdym razem odsłania
     inny fragment. */
  _glitch(rec, snd, gl, A) {
    /* limit jednoczesnych ziaren obowiązuje, ale odstępu nie pilnujemy —
       glitch jest na tyle rzadki, że nie ma go co racjonować.
       Kolejkę ruszamy dopiero po limicie, żeby odrzucone rozdarcie
       nie zjadało kolejnej próbki */
    if (this._grains.length >= A.acMaxGrains) return;

    const url = this.banks.nextGlitch();
    if (!url) return;

    const buffer = this.engine.getBuffer(url);
    if (!buffer) { this.engine.load(url).catch(() => {}); return; }

    const dur = Math.min(Math.max(gl.dur || 0, 0.05), buffer.duration);
    const room = Math.max(0, buffer.duration - dur - 0.02);
    this._grain(rec, snd, url, A.glVolume, A, {
      offset: Math.random() * Math.min(A.glOffset, room),
      duration: dur,
      release: A.glRelease,
      rate: 1,                                  /* glitch bez rozstrajania */
    });
  }

  /* --- fale ----------------------------------------------------- */

  /* Wejście figury na scenę: jeden przeciągły dźwięk na cały jej pobyt.
     Limity sprawdzamy PRZED sięgnięciem po plik — kursor banku ma się
     ruszać tylko wtedy, gdy fala naprawdę zabrzmi, inaczej odrzucone
     figury wyrywałyby dziury z melodii. */
  _waveStart(rec, snd, spec, st, A) {
    if (rec.wave || this._waves >= A.flMaxVoices) return;
    const now = this.engine.ctx.currentTime;
    if (now - this._lastWaveAt < A.flMinGap) return;
    /* przerzedzenie stałe dla figury (a nie losowane co wejście), żeby
       ta sama figura nie migotała przy scrollowaniu w tę i we w tę;
       liczone na żywo, bo flChance kręci się w panelu */
    if (hash01(rec.fig.seed + 53) >= A.flChance) return;

    if (!snd.fala) snd.fala = this.banks.nextFala(rec.fig);
    if (!snd.fala) return;                       /* kształt bez fali */

    const buffer = this.engine.getBuffer(snd.fala);
    if (!buffer) { this.engine.load(snd.fala).catch(() => {}); return; }

    rec.wave = this.engine.play(buffer, {
      bus: rec.chain,
      volume: lerp(A.flVolMin, A.flVolMax, prominence(rec.fig, spec, st)) * snd.gainJit,
      fadeIn: A.flFadeIn,
      /* figura potrafi falować dłużej niż trwa plik, więc dźwięk nawija
         się w kółko zakładką na siebie (jak akordy tła) — przy
         flOverlap = 0 wraca zachowanie jednorazowe */
      overlap: A.flOverlap,
      /* dochodzi tylko przy flOverlap = 0; przedłużana fala nie kończy
         się sama, gasi ją dopiero wyjście figury */
      onEnded: (v) => this._waveEnded(rec, v),
    });
    if (rec.wave) { this._waves++; this._lastWaveAt = now; }
  }

  /* figura rusza do wyjścia — fala gaśnie, ale nie urywa się */
  _waveStop(rec, A) {
    const v = rec.wave;
    if (!v) return;
    rec.wave = null;
    this._waves = Math.max(0, this._waves - 1);
    const rel = Math.max(0.05, A.flRelease);
    v.stop(rel);
    setTimeout(() => v.destroy(), (rel + 0.2) * 1000);
  }

  _waveEnded(rec, v) {
    if (rec.wave === v) {
      rec.wave = null;
      this._waves = Math.max(0, this._waves - 1);
    }
    v.destroy();
  }

  /* pętla pod równomierny obrót */
  _clock(rec, snd, A) {
    if (rec.clock || !snd.zegar || this._clocks >= A.acMaxClocks) return;
    const buffer = this.engine.getBuffer(snd.zegar);
    if (!buffer) return;

    rec.clock = this.engine.play(buffer, {
      bus: rec.chain,
      volume: A.acClockVol,
      loop: true,
      rate: 1 + (snd.rng() - 0.5) * 2 * A.acDetune,
      fadeIn: 1.2,
    });
    if (rec.clock) this._clocks++;
  }

  /* --- ziarna --------------------------------------------------- */

  /* wspólny limit: nie za gęsto i nie za wiele naraz */
  _budget(now, A) {
    if (this._grains.length >= A.acMaxGrains) return false;
    if (now - this._lastGrainAt < A.acMinGap) return false;
    return true;
  }

  /**
   * @param {object} [extra] — nadpisania dla głosu (np. przycięcie glitcha)
   */
  _grain(rec, snd, url, weight, A, extra) {
    const buffer = this.engine.getBuffer(url);
    if (!buffer) {                                /* jeszcze się wczytuje */
      this.engine.load(url).catch(() => {});
      return false;
    }
    const voice = this.engine.play(buffer, {
      bus: rec.chain,
      volume: weight * lerp(0.82, 1, snd.rng()),  /* drobna wariacja siły */
      pan: (snd.rng() - 0.5) * 0.3,               /* drobne rozejście w planie */
      rate: 1 + (snd.rng() - 0.5) * 2 * A.acDetune,
      onEnded: (v) => this._release(v),
      ...extra,
    });
    if (!voice) return false;

    this._grains.push(voice);
    this._lastGrainAt = this.engine.ctx.currentTime;
    return true;
  }

  _release(voice) {
    const i = this._grains.indexOf(voice);
    if (i >= 0) this._grains.splice(i, 1);
    voice.destroy();
  }

  /* --- sprzątanie ----------------------------------------------- */

  /* figury, których nie ma już w kadrze, oddają swoje zasoby */
  _sweep() {
    for (const [fig, rec] of this._figs) {
      if (this._frame - rec.seen <= RELEASE_FRAMES) continue;
      this._dispose(rec, false);
      this._figs.delete(fig);
    }
  }

  _dispose(rec, now) {
    if (rec.wave) {
      const wave = rec.wave;
      this._waves = Math.max(0, this._waves - 1);
      rec.wave = null;
      if (now) wave.destroy();
      else {
        wave.stop(0.6);
        setTimeout(() => wave.destroy(), 900);
      }
    }
    if (rec.clock) {
      const clock = rec.clock;
      this._clocks = Math.max(0, this._clocks - 1);
      rec.clock = null;
      if (now) clock.destroy();
      else {
        clock.stop(0.8);
        setTimeout(() => clock.destroy(), 1100);
      }
    }
    if (rec.chain) {
      const chain = rec.chain;
      rec.chain = null;
      /* ziarna jeszcze wybrzmiewają — łańcuch znika chwilę po nich */
      if (now) chain.disconnect();
      else setTimeout(() => chain.disconnect(), CHAIN_LINGER_MS);
    }
  }
}

/* Jak bardzo figura rzuca się w oczy, 0–1 — stąd bierze się waga fali.
   Dwa składniki, zgodnie z tym, co widać: ile figura faluje (amplituda
   jej ruchu własnego, stała przez całe jej życie) i jak duża jest teraz
   na ekranie. Ruch waży więcej, bo to o falowaniu jest ten dźwięk.

   Wielkość liczy się tu drugi raz — łańcuch figury już raz ją uwzględnił
   (acGainMin w _shape) — i tak ma być: fale mają reagować na skalę figury
   wyraźniej niż ziarna, inaczej drobne patyczki dudnią na równi z panelem
   przez pół kadru. */
function prominence(fig, spec, st) {
  const move = clamp01((fig.idle.amp - WAVE_AMP_MIN) / (WAVE_AMP_MAX - WAVE_AMP_MIN));
  const dim = (fig.size.r ? fig.size.r * 2 : Math.max(fig.size.w, fig.size.h))
    * st.vminPx * spec.s;
  const size = clamp01(dim / (st.vminPx * 1.2));
  return clamp01(move * 0.6 + size * 0.4);
}

/* --- tożsamość dźwiękowa figury (trwa tak długo, jak figura) ---- */

function voiceOf(fig, banks, A) {
  let snd = fig._snd;
  if (snd) return snd;

  const rng = mulberry32(fig.seed + 7);
  snd = {
    /* czy figura w ogóle brzmi — decyzja stała, żeby ziarna nie
       migotały losowo w trakcie jednego drgania */
    voiced: hash01(fig.seed + 31) < A.acDensity,
    rng,
    gainJit: lerp(0.85, 1, rng()),
    tiki: null, tikiIdx: 0,
    exitOrder: null, exitIdx: 0,
    zegar: null,
    /* plik fali przypisujemy dopiero, gdy figura naprawdę zabrzmi
       (patrz _waveStart) — potem zostaje przy niej na stałe */
    fala: null,
  };
  if (snd.voiced) {
    snd.tiki = fig.pers === 'violent' ? banks.assignTiki(fig) : null;
    snd.exitOrder = banks.assignExit(fig);
    snd.zegar = fig.spinV ? banks.assignZegar(fig) : null;
  }
  fig._snd = snd;
  return snd;
}

/* tasowanie z gwarancją, że pierwszy element nie powtórzy poprzedniego */
function reshuffle(arr, rng, avoid) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = a[i]; a[i] = a[j]; a[j] = t;
  }
  if (a.length > 1 && a[0] === avoid) {
    const t = a[0]; a[0] = a[1]; a[1] = t;
  }
  return a;
}

export default AccentLayer;
